/**
 * Scelta di colore e taglia sopra le schede dell'offerta LeiDry (pallini e quadratini, come LeiShape).
 *
 * Non tocca la logica dell'offerta: comanda le tendine delle righe di ldy-offerta con eventi `change`,
 * così prezzi, galleria, campo `id` e aggiunta al carrello restano quelli di ldy-offerta.js.
 * - Un cambio qui scrive quel valore in tutte e tre le righe (la riga 1 è il paio della scheda «1 paio»,
 *   le righe 2 e 3 sono i default del 2+1): come il bundle di LeiShape.
 * - Un cambio a mano della riga 1 dentro il 2+1 si riflette qui, così colore e taglia mostrati sono
 *   sempre quelli del paio n. 1 (e della foto).
 * Elemento semplice (non un Component di Horizon): niente `ref` né `on:` che finirebbero all'offerta.
 */

/**
 * @typedef {Object} Variante
 * @property {number} id
 * @property {string[]} opzioni
 * @property {boolean} disponibile
 */

class LdySceltaElement extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLElement | null} */
  #offerta = null;
  /** @type {{ opzioni: string[], varianti: Variante[] } | null} */
  #dati = null;
  /** @type {Promise<DocumentFragment> | null} */
  #guida = null;
  /** @type {HTMLDialogElement | null} */
  #finestra = null;
  /** @type {HTMLElement | null} */
  #rientro = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#offerta = this.closest('ldy-offerta-component');
    const sorgente = this.#offerta?.querySelector('script[type="application/json"][ref="dati"]');
    try {
      this.#dati = JSON.parse(sorgente?.textContent || 'null');
    } catch {
      this.#dati = null;
    }

    const finestra = this.querySelector('dialog[data-guida]');
    this.#finestra = finestra instanceof HTMLDialogElement ? finestra : null;

    this.addEventListener('change', (event) => this.#suScelta(event), { signal });
    this.addEventListener('click', (event) => this.#suClic(event), { signal });
    this.addEventListener('pointerdown', (event) => this.#prepara(event), { signal, passive: true });
    this.addEventListener('focusin', (event) => this.#prepara(event), { signal });
    this.#offerta?.addEventListener('change', (event) => this.#suRiga(event), { signal });
    this.#finestra?.addEventListener('close', () => this.#suChiusura(), { signal });

    this.#allinea();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {HTMLElement[]} Le righe dell'offerta (1, 2, 3) in ordine. */
  #righe() {
    if (!this.#offerta) return [];
    return /** @type {HTMLElement[]} */ (Array.from(this.#offerta.querySelectorAll('[data-riga]'))).sort(
      (a, b) => Number(a.dataset.riga) - Number(b.dataset.riga)
    );
  }

  /**
   * Valori del paio n. 1 nell'ordine delle opzioni (le opzioni senza tendina prendono il valore della prima variante).
   * @returns {string[]}
   */
  #valori() {
    const dati = this.#dati;
    if (!dati) return [];
    const prima = this.#righe()[0];
    return dati.opzioni.map((_, indice) => {
      const select = prima?.querySelector(`select[data-opzione="${indice}"]`);
      if (select instanceof HTMLSelectElement) return select.value;
      const radio = this.querySelector(`input[data-opzione="${indice}"]:checked`);
      if (radio instanceof HTMLInputElement) return radio.value;
      return dati.varianti[0]?.opzioni[indice] ?? '';
    });
  }

  /**
   * Stato di un valore dato quello delle opzioni precedenti (a cascata, come le tendine dell'offerta).
   * @param {number} indice
   * @param {string} valore
   * @param {string[]} valori
   * @returns {'' | 'esaurito' | 'assente'}
   */
  #stato(indice, valore, valori) {
    const candidati = (this.#dati?.varianti ?? []).filter(
      (variante) =>
        variante.opzioni[indice] === valore && variante.opzioni.every((v, i) => i >= indice || v === valori[i])
    );
    if (!candidati.length) return 'assente';
    return candidati.some((variante) => variante.disponibile) ? '' : 'esaurito';
  }

  /**
   * Clic su un pallino o un quadratino.
   * @param {Event} event
   */
  #suScelta(event) {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio' || !radio.checked || !this.#dati) return;
    event.stopPropagation();

    const indice = Number(radio.dataset.opzione);
    const valori = this.#valori();
    valori[indice] = radio.value;

    // Le opzioni dopo quella cambiata restano com'erano se la combinazione si può comprare,
    // altrimenti passano al primo valore disponibile (es. colore senza più quella taglia).
    for (let i = indice + 1; i < valori.length; i += 1) {
      const attuale = valori[i] ?? '';
      if (this.#stato(i, attuale, valori) === '') continue;
      const libero = this.#valoriOpzione(i).find((valore) => this.#stato(i, valore, valori) === '');
      if (libero) valori[i] = libero;
    }

    const prima = this.#valori();
    valori.forEach((valore, i) => {
      if (i === indice || valore !== prima[i]) this.#scriviRighe(i, valore);
    });

    this.#allinea();
  }

  /**
   * @param {number} indice
   * @returns {string[]} I valori di un'opzione nell'ordine dei bottoni.
   */
  #valoriOpzione(indice) {
    return Array.from(this.querySelectorAll(`input[data-opzione="${indice}"]`)).map(
      (radio) => /** @type {HTMLInputElement} */ (radio).value
    );
  }

  /**
   * Scrive un valore nelle tre righe dell'offerta. Le righe 2 e 3 prima, la riga 1 per ultima
   * (è lei che aggiorna campo `id`, galleria e prezzi). Le righe 2 e 3 non diventano «cambiate a mano».
   * @param {number} indice
   * @param {string} valore
   */
  #scriviRighe(indice, valore) {
    const [prima, ...altre] = this.#righe();
    for (const riga of [...altre, prima]) {
      if (!riga) continue;
      const select = riga.querySelector(`select[data-opzione="${indice}"]`);
      if (!(select instanceof HTMLSelectElement) || select.value === valore) continue;
      const segno = riga.dataset.modificata;
      select.value = valore;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      if (segno === undefined) delete riga.dataset.modificata;
      else riga.dataset.modificata = segno;
    }
  }

  /**
   * Cambio a mano di una tendina dell'offerta: se è la riga 1, si riallinea la scelta in alto.
   * @param {Event} event
   */
  #suRiga(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.closest('[data-riga="1"]')) return;
    this.#allinea();
  }

  /** Bottoni scelti, valori nelle etichette e valori esauriti, dal paio n. 1. */
  #allinea() {
    if (!this.#dati) return;
    const valori = this.#valori();

    for (const gruppo of this.querySelectorAll('[data-scelta-opzione]')) {
      if (!(gruppo instanceof HTMLElement)) continue;
      const indice = Number(gruppo.dataset.sceltaOpzione);
      const scelto = valori[indice] ?? '';

      const etichetta = gruppo.querySelector('[data-valore]');
      if (etichetta && etichetta.textContent !== scelto) etichetta.textContent = scelto;

      for (const radio of gruppo.querySelectorAll('input[type="radio"]')) {
        if (!(radio instanceof HTMLInputElement)) continue;
        const stato = this.#stato(indice, radio.value, valori);
        const attivo = radio.value === scelto;
        if (radio.checked !== attivo) radio.checked = attivo;
        radio.disabled = Boolean(stato) && !attivo;
        const voce = radio.closest('label');
        if (voce) {
          voce.classList.toggle('is-scelto', attivo);
          voce.classList.toggle('is-esaurito', Boolean(stato));
          if (stato) voce.dataset.stato = stato;
          else delete voce.dataset.stato;
        }
      }
    }
  }

  /** @param {Event} event */
  #suClic(event) {
    const elemento = event.target instanceof Element ? event.target : null;
    if (!elemento || !this.#finestra) return;

    if (elemento.closest('[data-guida-apri]')) {
      event.preventDefault();
      this.#apri(/** @type {HTMLElement} */ (elemento.closest('[data-guida-apri]')));
      return;
    }

    if (elemento.closest('[data-guida-chiudi]') || elemento === this.#finestra) {
      // Il clic sullo sfondo arriva con target = <dialog> (il contenuto è dentro la scatola interna).
      this.#finestra.close();
    }
  }

  /**
   * Scarica la guida appena la mano si avvicina al bottone.
   * @param {Event} event
   */
  #prepara(event) {
    if (event.target instanceof Element && event.target.closest('[data-guida-apri]')) this.#caricaGuida();
  }

  /** @param {HTMLElement} bottone */
  #apri(bottone) {
    const finestra = this.#finestra;
    if (!finestra || finestra.open) return;
    this.#rientro = bottone;
    finestra.showModal();
    document.documentElement.classList.add('ldy-guida-aperta');
    this.#riempi();
  }

  #suChiusura() {
    document.documentElement.classList.remove('ldy-guida-aperta');
    this.#rientro?.focus({ preventScroll: true });
    this.#rientro = null;
  }

  async #riempi() {
    const corpo = this.#finestra?.querySelector('[data-guida-corpo]');
    if (!(corpo instanceof HTMLElement) || corpo.dataset.pronta === 'true') return;
    try {
      const frammento = await this.#caricaGuida();
      corpo.replaceChildren(frammento.cloneNode(true));
      corpo.dataset.pronta = 'true';
    } catch (errore) {
      this.#guida = null;
      const errorePronto = corpo.querySelector('[data-guida-errore]');
      corpo.querySelector('[data-guida-attesa]')?.setAttribute('hidden', '');
      errorePronto?.removeAttribute('hidden');
      console.warn('[ldy-scelta] Guida alle taglie non caricata.', errore);
    }
  }

  /**
   * Legge la pagina della guida alle taglie (stesse tabelle, niente misure scritte due volte) e tiene
   * solo le tabelle di questo prodotto (le parole del titolo del prodotto nel titolo della tabella).
   * @returns {Promise<DocumentFragment>}
   */
  #caricaGuida() {
    if (this.#guida) return this.#guida;
    const url = this.#finestra?.dataset.url;
    if (!url) return Promise.reject(new Error('Pagina della guida mancante'));

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);

    const scarica = () => fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal });
    this.#guida = scarica()
      .then((risposta) =>
        risposta.status === 429 ? new Promise((fatto) => setTimeout(fatto, 1500)).then(scarica) : risposta
      )
      .then((risposta) => {
        if (!risposta.ok) throw new Error(`HTTP ${risposta.status}`);
        return risposta.text();
      })
      .then((testo) => {
        const pagina = new DOMParser().parseFromString(testo, 'text/html');
        const tabelle = Array.from(pagina.querySelectorAll('.ldy-taglie__tabella'));
        if (!tabelle.length) throw new Error('Nessuna tabella nella pagina');

        const parole = paroleDi(this.dataset.prodotto ?? '');
        const punteggi = tabelle.map((tabella) => {
          const titolo = paroleDi(tabella.querySelector('.ldy-taglie__titolo')?.textContent ?? '');
          return parole.filter((parola) => titolo.includes(parola)).length;
        });
        const migliore = Math.max(...punteggi);
        const scelte = migliore > 0 ? tabelle.filter((_, i) => punteggi[i] === migliore) : tabelle;

        const frammento = document.createDocumentFragment();
        for (const nodo of [...scelte, pagina.querySelector('.ldy-taglie__misure')]) {
          if (!nodo) continue;
          for (const vietato of nodo.querySelectorAll('script, style, iframe')) vietato.remove();
          frammento.append(document.importNode(nodo, true));
        }
        return frammento;
      })
      .finally(() => clearTimeout(timer));

    return this.#guida;
  }
}

/**
 * Parole utili per abbinare prodotto e tabella (minuscole, almeno 4 lettere).
 * @param {string} testo
 * @returns {string[]}
 */
function paroleDi(testo) {
  return testo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((parola) => parola.length >= 4 && !['taglia', 'taglie', 'tabella'].includes(parola));
}

if (!customElements.get('ldy-scelta')) {
  customElements.define('ldy-scelta', LdySceltaElement);
}

/* Link «Guida alle taglie ›» fuori dalla scelta (blocco ldy-riquadro-taglie): apre la stessa finestra della scelta,
   se nella pagina c'è; altrimenti il link porta alla pagina della guida. */
if (!window.ldyGuidaFuori) {
  window.ldyGuidaFuori = true;
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-ldy-guida-apri]') : null;
    if (!link) return;
    const contenitore = link.closest('.shopify-section') ?? document;
    const bottone = contenitore.querySelector('ldy-scelta [data-guida-apri]');
    if (!(bottone instanceof HTMLElement)) return;
    event.preventDefault();
    bottone.click();
  });
}
